import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { BadgeRenderer } from '../badges/BadgeRenderer';
import type { IssuedBadge } from '../../types/discoveryBadges';
import type { Achievement } from '../../types/buildExpansion';
import { Button } from '../ui/Button';
import LimitedDesignationPlate from '../limited-designation/LimitedDesignationPlate';
import AchievementCollection from '../expansion/AchievementCollection';

interface VehicleRecognitionSectionProps {
  vehicleId: number;
  isOwner?: boolean;
  awards?: any[];
  achievements?: Achievement[];
  limitedDesignations?: Array<{
    public_id: string;
    name: string;
    ordinal: number;
    edition_size: number;
    ordinal_label?: string;
    title?: string;
    issuer_label?: string;
    public_blurb?: string | null;
    description?: string | null;
    issued_at?: string;
  }>;
}

/**
 * Filters map to distinct recognition sources:
 * - awards / events / clubs → discovery badges (+ event award rows)
 * - achievements → platform + verified credentials
 * - milestones → build milestones (documented progress), not badge_category=milestone
 */
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'awards', label: 'Awards' },
  { id: 'events', label: 'Events' },
  { id: 'clubs', label: 'Clubs' },
  { id: 'achievements', label: 'Achievements' },
  { id: 'milestones', label: 'Milestones' },
] as const;

type FilterId = (typeof FILTERS)[number]['id'];

const badgeApiCategory = (filter: FilterId): string | undefined => {
  if (filter === 'all') return 'all';
  if (filter === 'awards' || filter === 'events' || filter === 'clubs') return filter;
  return undefined;
};

const VehicleRecognitionSection: React.FC<VehicleRecognitionSectionProps> = ({
  vehicleId,
  isOwner = false,
  awards = [],
  achievements = [],
  limitedDesignations = [],
}) => {
  const [badges, setBadges] = useState<IssuedBadge[]>([]);
  const [card, setCard] = useState<{ badges: IssuedBadge[]; overflow: number } | null>(null);
  const [filter, setFilter] = useState<FilterId>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const showBadgePane = filter === 'all' || filter === 'awards' || filter === 'events' || filter === 'clubs';
  const showAwardsList = filter === 'all' || filter === 'awards';
  const showLimited = filter === 'all' || filter === 'awards';
  const showAchievementsPane = filter === 'all' || filter === 'achievements' || filter === 'milestones';

  useEffect(() => {
    if (!showBadgePane) {
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const category = badgeApiCategory(filter);
        const data = await apiService.getVehicleBadges(vehicleId, {
          category,
          include_revoked: isOwner,
        });
        if (!cancelled) {
          setBadges(data?.badges || []);
          setCard(data?.card || null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load recognition');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, filter, isOwner, showBadgePane]);

  const awardWithBadge = (award: any) => {
    const match = badges.find((b) => b.award_id && Number(b.award_id) === Number(award.id));
    return match;
  };

  const visibleBadges = badges.filter((b) => b.is_active || isOwner);
  const filteredAchievements = useMemo(() => {
    if (filter === 'milestones') {
      return achievements.filter((a) => a.achievement_class === 'build_milestone');
    }
    if (filter === 'achievements') {
      return achievements.filter(
        (a) =>
          a.achievement_class === 'platform_achievement' ||
          a.achievement_class === 'verified_credential' ||
          a.achievement_class == null
      );
    }
    return achievements;
  }, [achievements, filter]);

  const hasAwards = showAwardsList && awards.length > 0;
  const hasBadges = showBadgePane && visibleBadges.length > 0;
  const hasLimited = showLimited && limitedDesignations.length > 0;
  const hasAchievements = showAchievementsPane && filteredAchievements.length > 0;
  const showSection =
    loading ||
    Boolean(error) ||
    hasAwards ||
    hasBadges ||
    hasLimited ||
    achievements.length > 0 ||
    filter !== 'all' ||
    isOwner;

  if (!showSection) {
    return null;
  }

  const emptyLabel = FILTERS.find((f) => f.id === filter)?.label.toLowerCase() ?? 'items';
  const showEmpty =
    !loading &&
    !error &&
    !hasAwards &&
    !hasBadges &&
    !hasLimited &&
    !hasAchievements &&
    !(filter === 'all' && card && card.badges.length > 0);

  return (
    <section className="mt-12 sm:mt-14" aria-labelledby="recognition-heading">
      <div className="border-b border-white/[0.06] pb-5 mb-7">
        <p className="kicker mb-2">Trophy case</p>
        <h2
          id="recognition-heading"
          className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white"
        >
          Recognition
        </h2>
        <p className="text-sm text-gray-500 mt-2 max-w-[48ch] leading-relaxed">
          Awards and event badges, club affiliations, platform achievements, and documented
          milestones for this build.
        </p>
      </div>

      <div
        className="flex gap-2 overflow-x-auto mb-6 pb-1"
        role="toolbar"
        aria-label="Recognition filters"
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={filter === f.id ? 'filter-chip-active' : 'filter-chip'}
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && showBadgePane ? (
        <p className="text-sm text-gray-500 mb-4" role="status">
          Loading recognition…
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-red-300 text-sm mb-4">
          {error}
        </p>
      ) : null}

      {hasLimited ? (
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.16em] text-gray-500 mb-3">Limited designation</p>
          <ul className="flex flex-wrap gap-3">
            {limitedDesignations.map((d) => (
              <li key={d.public_id}>
                <LimitedDesignationPlate designation={d} size="full" />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showBadgePane && card && card.badges.length > 0 && filter === 'all' ? (
        <div className="mb-7">
          <h3 className="spec-label mb-3">Featured</h3>
          <div className="flex flex-wrap gap-3">
            {card.badges.map((b) => (
              <Link key={b.public_id} to={`/badges/${b.public_id}`} className="inline-flex">
                <BadgeRenderer design={b.design} size="md" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {hasAwards ? (
        <div className="mb-7">
          <h3 className="spec-label mb-3">Event awards</h3>
          <div className="space-y-2" data-testid="vehicle-awards-list">
            {awards.map((a) => {
              const badge = awardWithBadge(a);
              return (
                <div
                  key={a.id}
                  data-testid={`vehicle-award-${a.id}`}
                  className="section-surface flex items-center gap-4 rounded-xl px-4 py-3.5"
                  style={
                    {
                      ['--section-color' as string]: '#F97316',
                      ['--section-border' as string]: 'rgba(249,115,22,0.45)',
                      ['--section-tint' as string]: 'rgba(249,115,22,0.08)',
                      borderColor: 'rgba(249,115,22,0.45)',
                      backgroundColor: 'rgba(249,115,22,0.08)',
                    } as React.CSSProperties
                  }
                >
                  {badge ? <BadgeRenderer design={badge.design} size="sm" /> : null}
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-white text-base font-semibold tracking-tight truncate">
                      {a.title}
                    </p>
                    <p className="font-mono text-xs text-gray-400 mt-0.5">
                      {[a.placement, a.event_title].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {badge ? (
                    <Link
                      to={`/badges/${badge.public_id}`}
                      className="text-xs font-medium text-orange-300 hover:text-orange-200 transition-colors shrink-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400"
                    >
                      Details
                    </Link>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {hasBadges ? (
        <div className="mb-8">
          {filter === 'all' ? <h3 className="spec-label mb-3">Badges</h3> : null}
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 list-none">
            {visibleBadges.map((b) => (
              <li key={b.public_id}>
                <Link
                  to={`/badges/${b.public_id}`}
                  className={`rounded-xl bg-[#0c0c0c]/50 ring-1 ring-white/5 hover:ring-white/15 transition-shadow duration-200 p-3 flex items-center gap-2.5 h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 ${
                    !b.is_active ? 'opacity-50' : ''
                  }`}
                >
                  <BadgeRenderer design={b.design} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {b.design?.name || b.definition?.name}
                    </p>
                    <p className="font-mono text-[11px] text-gray-500 capitalize mt-0.5">
                      {b.is_active ? b.verification_type?.replace(/_/g, ' ') : 'Revoked'}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasAchievements ? (
        <div className={filter === 'all' ? 'mt-2' : ''}>
          <AchievementCollection
            achievements={filteredAchievements}
            className={filter === 'all' ? '' : 'border-0 bg-transparent p-0 sm:p-0'}
            hideIntro={filter !== 'all'}
            focus={
              filter === 'milestones'
                ? 'milestones'
                : filter === 'achievements'
                  ? 'achievements'
                  : 'all'
            }
          />
        </div>
      ) : null}

      {showEmpty ? (
        <p className="text-sm text-gray-500">
          {filter === 'all'
            ? 'No recognition yet for this build.'
            : `No ${emptyLabel} to show.`}
        </p>
      ) : null}

      {isOwner && showBadgePane && badges.length > 0 ? (
        <div className="mt-4">
          <Button
            variant="ghost"
            onClick={async () => {
              const ids = badges.filter((b) => b.is_active).slice(0, 3).map((b) => b.id);
              await apiService.setFeaturedBadges(vehicleId, ids);
            }}
          >
            Feature top three by prestige
          </Button>
        </div>
      ) : null}
    </section>
  );
};

export default VehicleRecognitionSection;
